'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { X, Search, Users } from 'lucide-react';

interface SurnameSearchPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SurnameSearchPopup({ isOpen, onClose }: SurnameSearchPopupProps) {
  const router = useRouter();
  const [searchSurname, setSearchSurname] = useState('');
  const [isValidating, setIsValidating] = useState(false);

  const sampleSurnames = [
    'Shinde', 'Patil', 'Kulkarni', 'Mhatre', 'Deshmukh', 
    'Jadhav', 'Pawar', 'More', 'Chavan', 'Gaikwad',
    'Salunkhe', 'Dhumal', 'Bhosale', 'Nimbalkar', 'Rane'
  ];

  const handleSearch = () => {
    if (!searchSurname.trim()) return;

    setIsValidating(true);

    // Simulate search (replace with actual API call later)
    setTimeout(() => {
      setIsValidating(false);
      // TODO: Implement actual search logic
      console.log('Searching for surname:', searchSurname);
      
      // Navigate to search results page with surname filter
      onClose();
      router.push(`/search-results?surname=${encodeURIComponent(searchSurname)}`);
    }, 1000);
  };

  const handleSurnameClick = (surname: string) => {
    setSearchSurname(surname);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Search by Surname</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            <label htmlFor="searchSurname" className="block text-sm font-medium text-gray-700 mb-2">
              Enter Surname
            </label>
            <div className="relative">
              <input
                type="text"
                id="searchSurname"
                value={searchSurname}
                onChange={(e) => setSearchSurname(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter surname to search"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg"
                autoFocus
              />
            </div>
          </div>

          {/* Sample Surnames */}
          <div className="mb-6">
            <div className="flex items-center space-x-2 mb-3">
              <Users className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Popular Surnames</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {sampleSurnames.map((surname) => (
                <button
                  key={surname}
                  onClick={() => handleSurnameClick(surname)}
                  className="px-3 py-2 text-sm bg-gray-100 hover:bg-primary-100 hover:text-primary-700 text-gray-700 rounded-md transition-colors border border-gray-200 hover:border-primary-300"
                >
                  {surname}
                </button>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Click on any surname above to quickly fill the search field
            </p>
          </div>

          {/* Search Button */}
          <button
            onClick={handleSearch}
            disabled={!searchSurname.trim() || isValidating}
            className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isValidating ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Searching...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center space-x-2">
                <Search className="w-5 h-5" />
                <span>Search Profiles</span>
              </div>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
